/* eslint-disable @typescript-eslint/no-unused-vars */

'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { buttonThemes } from './buttonThemes';
import { BuilderContent, BuilderElement } from '@builder.io/react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import type { Route } from 'next';

interface DesignerButtonProps extends HTMLMotionProps<'button'> {
  text?: React.ReactNode;
  variant?: keyof typeof buttonThemes;
  outline?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  type?: 'button' | 'submit' | 'reset';
  href?: Route;
  builderState?: BuilderContent;
  builderBlock?: BuilderElement;
  builderTag?: string;
}

const DesignerButton: React.FC<DesignerButtonProps> = ({
  text = 'Button',
  variant = 'primary',
  outline = false,
  size = 'medium',
  className,
  disabled,
  ariaLabel,
  type = 'button',
  href,
  builderState,
  builderBlock,
  builderTag,
  ...props
}) => {
  const sizeStyles = {
    small:
      'w-full tablet:w-auto tablet:min-w-[240px] px-4 py-2 text-xs h-[36px]',
    medium:
      'w-full tablet:w-auto tablet:min-w-[260px] px-6 py-3 text-sm h-[44px]',
    large:
      'w-full tablet:w-auto tablet:min-w-[300px] px-8 py-4 text-base h-[52px]',
  };

  const router = useRouter();
  const [hovered, setHovered] = useState(false);

  // Provide fallback for undefined variants
  const defaultTheme = {
    initial: 'bg-black text-white',
    hoverBackground: 'bg-gray-100',
    hoverText: 'text-black font-semibold',
    border: 'border border-white',
    hoverBorder: 'border-black',
  };

  const theme =
    variant && buttonThemes[variant] ? buttonThemes[variant] : defaultTheme;
  const sizeClass = sizeStyles[size];

  const buttonStyles =
    `relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent rounded
  transition-all duration-300 ease-in-out flex items-center justify-center font-light
  shadow-button-3d hover:shadow-button-3d-hover
  bg-button-gradient backdrop-blur-[2px]
  transform hover:-translate-y-0.5 active:translate-y-0
  ${theme.initial} ${outline ? theme.border : ''} ${sizeClass} ${
      className || ''
    }`.trim();

  const content = (
    <>
      <motion.span
        initial={{ x: '-100%' }}
        animate={{ x: hovered ? '0%' : '-100%' }}
        transition={{ duration: 0.2 }}
        className={`absolute inset-0 ${theme.hoverBackground} ${theme.hoverBorder} z-0 
    before:absolute before:inset-0 before:bg-button-gradient before:opacity-50`}
      />
      <span className={`relative z-10 ${hovered ? theme.hoverText : ''}`}>
        {text}
      </span>
    </>
  );

  const handleMouseDown = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    event.currentTarget.blur();
  };

  const handleClick = () => {
    if (href) {
      router.push(href);
    }
  };

  if (href) {
    return (
      <Link
        href={href}
        className={buttonStyles}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {content}
      </Link>
    );
  }

  return (
    <motion.button
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={handleClick}
      type={type}
      className={buttonStyles}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      onMouseDown={handleMouseDown}
      {...props}
    >
      {content}
    </motion.button>
  );
};

export default DesignerButton;
